import * as React from 'react';
import {
    Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogContentText, DialogTitle, LinearProgress, Stack, Table, TableBody, TableCell, TableHead, TableRow,
    Tooltip, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { consolidateUserAchievements, getUserAchievements, openUserAchievement, resetUserAchievement } from '../Api';
import UserAchievementsAdminDto, { AdminUserAchievementDto } from '../models/dtos/UserAchievementsAdminDto';

/** Tier artwork copied from betup-ios-new `assets/images/trophy_room/trophy_counter_*.png` (48 px masters). */
const TIERS: Record<number, { label: string; icon: string; color: string }> = {
    1: { label: 'Bronze', icon: '/achievements/trophy_bronze.png', color: '#b87333' },
    2: { label: 'Silver', icon: '/achievements/trophy_silver.png', color: '#9aa4ad' },
    3: { label: 'Golden', icon: '/achievements/trophy_golden.png', color: '#d4a017' },
    4: { label: 'Diamond', icon: '/achievements/trophy_diamond.png', color: '#4fc3f7' },
};

/** After "open" the legacy row is flipped by the crons listener; poll briefly so the admin sees it land. */
const OPEN_POLL_ATTEMPTS = 8;
const OPEN_POLL_INTERVAL_MS = 1000;

type PendingAction = { kind: 'open' | 'reset'; item: AdminUserAchievementDto } | null;

function TierBadge({ type, size = 28 }: { type: number; size?: number }) {
    const tier = TIERS[type];
    if (!tier) return <Chip size="small" label={`type ${type}`} />;
    return (
        <Tooltip title={tier.label}>
            <Stack direction="row" alignItems="center" gap={0.75}>
                <img src={tier.icon} alt={tier.label} width={size} height={size} style={{ imageRendering: 'auto' }} />
                <Typography variant="body2" sx={{ color: tier.color, fontWeight: 600 }}>{tier.label}</Typography>
            </Stack>
        </Tooltip>
    );
}

function taskChipColor(status?: string): 'default' | 'success' | 'info' | 'warning' | 'error' {
    switch (status) {
        case 'COMPLETED': return 'success';
        case 'IN_PROGRESS': return 'info';
        case 'ASSIGNED': return 'default';
        case 'EXPIRED':
        case 'CANCELLED': return 'error';
        default: return 'warning';
    }
}

function formatDate(value?: string | null) {
    return value ? new Date(value).toLocaleString() : '—';
}

export default function UserAchievementsTable({ userId }: { userId: number }) {
    const [data, setData] = React.useState<UserAchievementsAdminDto | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [notice, setNotice] = React.useState<string | null>(null);
    const [pending, setPending] = React.useState<PendingAction>(null);
    const [awaitingUnlock, setAwaitingUnlock] = React.useState<Set<number>>(new Set());
    /** Category keys that are collapsed; everything is expanded until the admin folds it. */
    const [collapsed, setCollapsed] = React.useState<Set<string>>(new Set());
    const pollTimer = React.useRef<number | null>(null);

    const load = React.useCallback(async () => {
        setLoading(true);
        const result = await getUserAchievements(userId);
        setLoading(false);
        if (result == null) {
            setError('Failed to load achievements.');
            return;
        }
        setError(null);
        setData(result);
    }, [userId]);

    React.useEffect(() => {
        load();
        return () => {
            if (pollTimer.current != null) window.clearTimeout(pollTimer.current);
        };
    }, [load]);

    /** Re-fetches until the crons listener has flipped the legacy row (or we give up and leave the hint). */
    const pollUntilOpened = (achievementId: number, attemptsLeft: number) => {
        pollTimer.current = window.setTimeout(async () => {
            const fresh = await getUserAchievements(userId);
            if (fresh != null) {
                setData(fresh);
                const opened = fresh.categories.flatMap(c => c.items).find(i => i.achievement_id === achievementId)?.is_opened;
                if (opened || attemptsLeft <= 1) {
                    setAwaitingUnlock(prev => {
                        const next = new Set(prev);
                        next.delete(achievementId);
                        return next;
                    });
                    if (!opened) {
                        setNotice('Task completed, but the legacy row is still locked — is the crons achievements listener running?');
                    }
                    return;
                }
            }
            pollUntilOpened(achievementId, attemptsLeft - 1);
        }, OPEN_POLL_INTERVAL_MS);
    };

    const runAction = async () => {
        if (pending == null) return;
        const { kind, item } = pending;
        setPending(null);
        setBusy(true);
        setNotice(null);
        const result = kind === 'open'
            ? await openUserAchievement(userId, item.achievement_id)
            : await resetUserAchievement(userId, item.achievement_id);
        setBusy(false);
        if (result.data == null) {
            setError(`Failed to ${kind} "${item.name}": ${result.error ?? 'unknown error'}`);
            return;
        }
        setError(null);
        setData(result.data);
        if (kind === 'open') {
            const nowOpened = result.data.categories.flatMap(c => c.items).find(i => i.achievement_id === item.achievement_id)?.is_opened;
            if (!nowOpened) {
                setAwaitingUnlock(prev => new Set(prev).add(item.achievement_id));
                pollUntilOpened(item.achievement_id, OPEN_POLL_ATTEMPTS);
            }
            setNotice(`Task for "${item.name}" completed; the unlock (row, ${item.price.toLocaleString()} betcoins, push) is applied by the achievements worker.`);
        } else {
            setNotice(`"${item.name}" was reset to locked / 0 %. Betcoins already paid for it are not taken back.`);
        }
    };

    const consolidate = async () => {
        setBusy(true);
        setNotice(null);
        const result = await consolidateUserAchievements(userId);
        setBusy(false);
        if (result.data == null) {
            setError(`Consolidation failed: ${result.error ?? 'unknown error'}`);
            return;
        }
        setError(null);
        setData(result.data);
        const added = result.data.consolidation;
        setNotice(added && (added.legacy_rows_added > 0 || added.tasks_added > 0)
            ? `Consolidated: added ${added.legacy_rows_added} achievement row(s) and ${added.tasks_added} user task(s).`
            : 'Consolidated: nothing was missing.');
    };

    const categoryKey = (group: { category: { id: number } | null }) => String(group.category?.id ?? 'uncategorised');
    const toggleCategory = (key: string) => setCollapsed(prev => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });

    const items = React.useMemo(() => data?.categories.flatMap(c => c.items) ?? [], [data]);
    const tierCounts = React.useMemo(() => [4, 3, 2, 1].map(type => ({
        type,
        unlocked: items.filter(i => i.achievement_reward_type === type && i.is_opened).length,
        total: items.filter(i => i.achievement_reward_type === type).length,
    })), [items]);

    if (loading && data == null) {
        return <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress /></Stack>;
    }

    return (
        <Stack gap={2}>
            <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
                <Button variant="contained" onClick={consolidate} disabled={busy}>
                    Consolidate achievements
                </Button>
                <Button variant="outlined" onClick={load} disabled={busy || loading}>Refresh</Button>
                <Button size="small" onClick={() => setCollapsed(new Set())} disabled={collapsed.size === 0}>Expand all</Button>
                <Button size="small" onClick={() => setCollapsed(new Set(data?.categories.map(categoryKey) ?? []))} disabled={data == null || collapsed.size === data.categories.length}>Collapse all</Button>
                {data && (
                    data.provisioned
                        ? <Chip size="small" color="success" label="Provisioned" />
                        : <Chip size="small" color="warning" label={`Missing ${data.summary.missing_rows} row(s) / ${data.summary.missing_tasks} task(s)`} />
                )}
                <Typography variant="body2" color="text.secondary">
                    Consolidate runs the same lazy provisioning as the app start (missing rows and tasks are added, counters backfilled).
                </Typography>
            </Stack>

            {data && (
                <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
                    <Chip label={`Unlocked ${data.summary.unlocked} / ${data.summary.total}`} />
                    {tierCounts.map(t => TIERS[t.type] && (
                        <Chip
                            key={t.type}
                            variant="outlined"
                            avatar={<img src={TIERS[t.type].icon} alt={TIERS[t.type].label} width={20} height={20} />}
                            label={`${TIERS[t.type].label} ${t.unlocked} / ${t.total}`}
                        />
                    ))}
                </Stack>
            )}

            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
            {notice && <Alert severity="info" onClose={() => setNotice(null)}>{notice}</Alert>}

            {data?.categories.map(group => {
                const key = categoryKey(group);
                return (
                <Accordion
                    key={key}
                    expanded={!collapsed.has(key)}
                    onChange={() => toggleCategory(key)}
                    disableGutters
                    variant="outlined"
                    TransitionProps={{ unmountOnExit: true }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Stack direction="row" alignItems="baseline" gap={1} sx={{ width: '100%', pr: 1 }}>
                            <Typography variant="h6">{group.category?.name ?? 'Without category'}</Typography>
                            {group.category?.descr && (
                                <Typography variant="body2" color="text.secondary">{group.category.descr}</Typography>
                            )}
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                                {group.items.filter(i => i.is_opened).length} / {group.items.length} unlocked
                            </Typography>
                        </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell width={110}>Tier</TableCell>
                                <TableCell>Achievement</TableCell>
                                <TableCell width={90} align="right">Reward</TableCell>
                                <TableCell width={190}>Status</TableCell>
                                <TableCell width={260}>User task</TableCell>
                                <TableCell width={170} align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {group.items.map(item => {
                                const task = item.task;
                                const waiting = awaitingUnlock.has(item.achievement_id);
                                const taskDone = task?.status === 'COMPLETED';
                                const inconsistent = task != null && taskDone !== item.is_opened && !waiting;
                                const cup = item.cup_photo_url ?? item.photo_url;
                                return (
                                    <TableRow key={item.achievement_id} sx={{ opacity: item.is_opened ? 1 : 0.85 }}>
                                        <TableCell><TierBadge type={item.achievement_reward_type} /></TableCell>
                                        <TableCell>
                                            <Stack direction="row" gap={1.5} alignItems="center">
                                                {cup && (
                                                    <img
                                                        src={cup}
                                                        alt=""
                                                        width={40}
                                                        height={40}
                                                        style={{ objectFit: 'contain', filter: item.is_opened ? 'none' : 'grayscale(1) opacity(0.6)' }}
                                                    />
                                                )}
                                                <Box>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {item.name}
                                                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                            #{item.achievement_id}
                                                        </Typography>
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">{item.descr}</Typography>
                                                </Box>
                                            </Stack>
                                        </TableCell>
                                        <TableCell align="right">{item.price.toLocaleString()}</TableCell>
                                        <TableCell>
                                            {item.user_achievement_id == null ? (
                                                <Chip size="small" color="warning" label="No row (not provisioned)" />
                                            ) : item.is_opened ? (
                                                <Chip size="small" color="success" label="Unlocked" />
                                            ) : (
                                                <Stack gap={0.5}>
                                                    <Stack direction="row" alignItems="center" gap={1}>
                                                        <Chip size="small" label={waiting ? 'Unlocking…' : 'Locked'} color={waiting ? 'info' : 'default'} />
                                                        <Typography variant="caption">{item.progress} %</Typography>
                                                    </Stack>
                                                    <LinearProgress variant="determinate" value={Math.min(100, Math.max(0, item.progress))} />
                                                </Stack>
                                            )}
                                            {inconsistent && (
                                                <Tooltip title={taskDone
                                                    ? 'Task is COMPLETED but the row is locked — "Open" replays the completion event.'
                                                    : 'Row is unlocked but the task is not COMPLETED — "Reset" then "Open" realigns them.'}>
                                                    <Chip size="small" color="warning" variant="outlined" label="Row ≠ task" sx={{ mt: 0.5 }} />
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {task ? (
                                                <Stack gap={0.25}>
                                                    <Stack direction="row" gap={1} alignItems="center">
                                                        <Chip size="small" color={taskChipColor(task.status)} label={task.status} />
                                                        <Typography variant="body2">{task.progress_current} / {task.progress_target}</Typography>
                                                    </Stack>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {task.template_code ?? task.task_type} · #{task.user_task_id} · {formatDate(task.updated_at)}
                                                    </Typography>
                                                </Stack>
                                            ) : item.has_task_template ? (
                                                <Chip size="small" color="warning" label="No task (not provisioned)" />
                                            ) : (
                                                <Tooltip title="No active ACH_* user task template exists for this achievement; it cannot be opened through the task platform.">
                                                    <Chip size="small" variant="outlined" label="No template" />
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Stack direction="row" gap={1} justifyContent="flex-end">
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    color="success"
                                                    disabled={busy || waiting || !item.has_task_template || (item.is_opened && taskDone)}
                                                    onClick={() => setPending({ kind: 'open', item })}
                                                >
                                                    Open
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="warning"
                                                    disabled={busy || waiting || (item.user_achievement_id == null && task == null)}
                                                    onClick={() => setPending({ kind: 'reset', item })}
                                                >
                                                    Reset
                                                </Button>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                    </AccordionDetails>
                </Accordion>
                );
            })}

            <Dialog open={pending != null} onClose={() => setPending(null)}>
                <DialogTitle>{pending?.kind === 'open' ? 'Open achievement' : 'Reset achievement'}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {pending?.kind === 'open'
                            ? `Open "${pending.item.name}" (#${pending.item.achievement_id}) for user ${userId}? The backing task is completed and the achievements worker unlocks the row, credits ${pending.item.price.toLocaleString()} betcoins and sends the unlock push — exactly like an organic unlock.`
                            : `Reset "${pending?.item.name}" (#${pending?.item.achievement_id}) for user ${userId}? The row goes back to locked / 0 % and the task to ASSIGNED / 0. Betcoins already paid are not taken back; the Grand Slam counter is decremented if it was unlocked.`}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPending(null)}>Cancel</Button>
                    <Button variant="contained" color={pending?.kind === 'open' ? 'success' : 'warning'} onClick={runAction}>
                        {pending?.kind === 'open' ? 'Open' : 'Reset'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}
